import { api } from "@/lib/api";
import type {
  CreateUserPayload,
  Env,
  UpdateUserPayload,
  UserDetail,
  UserListResponse,
} from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface UserFilters {
  q?: string;
  status?: string;
  role?: string;
  sort?: string;
  order?: string;
  page?: number;
  page_size?: number;
  env?: Env;
}

function userFilterParams(f: UserFilters): string {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.status) p.set("status", f.status);
  if (f.role) p.set("role", f.role);
  if (f.sort) p.set("sort", f.sort);
  if (f.order) p.set("order", f.order);
  if (f.page) p.set("page", String(f.page));
  if (f.page_size) p.set("page_size", String(f.page_size));
  if (f.env) p.set("env", f.env);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function useUsers(filters: UserFilters = {}) {
  return useQuery({
    queryKey: ["users", filters],
    queryFn: () => api<UserListResponse>(`/api/ops/v1/users/${userFilterParams(filters)}`),
  });
}

// Backend endpoints all read ?env= via _env_db(request) (api/ops/views.py:36)
// to route to the correct DB alias. Frontend MUST forward the env on every
// per-user mutation/fetch — otherwise backend defaults to 'prod' and a dev
// row with id=N opens the prod row with id=N (or 404s, depending on overlap).
function envQuery(env?: Env): string {
  return env ? `?env=${env}` : "";
}

export function useUser(encryptedId: string | null, env?: Env) {
  return useQuery({
    queryKey: ["users", encryptedId, env],
    queryFn: () => api<UserDetail>(`/api/ops/v1/users/${encryptedId}${envQuery(env)}`),
    enabled: !!encryptedId,
  });
}

export function useCreateUser(env?: Env) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserPayload) =>
      api(`/api/ops/v1/users/create${envQuery(env)}`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useUpdateUser(env?: Env) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; data: UpdateUserPayload }) =>
      api(`/api/ops/v1/users/${params.id}/update${envQuery(env)}`, {
        method: "PATCH",
        body: JSON.stringify(params.data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useDeleteUser(env?: Env) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/ops/v1/users/${id}/delete${envQuery(env)}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useResetPassword(env?: Env) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; newPassword: string }) =>
      api(`/api/ops/v1/users/${params.id}/reset-password${envQuery(env)}`, {
        method: "POST",
        body: JSON.stringify({ new_password: params.newPassword }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface UnlockLoginResponse {
  status: string;
  message: string;
  data: { email: string; was_locked: boolean };
}

export function useUnlockLogin(env?: Env) {
  return useMutation({
    mutationFn: (email: string) =>
      api<UnlockLoginResponse>(`/api/ops/v1/auth/unlock${envQuery(env)}`, {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
  });
}

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export function useUploadAvatar(env?: Env) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; file: File }) => {
      const form = new FormData();
      form.append("avatar", params.file);
      const resp = await fetch(`${BASE}/api/ops/v1/users/${params.id}/avatar${envQuery(env)}`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        throw new Error((body as { error?: string })?.error ?? `Upload failed (${resp.status})`);
      }
      return resp.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}
